import { LineItem } from "@/types/schemas/line_items";

export type PositionGroup = {
  positionName: string;
  items: LineItem[];
  subtotal: number;
};

export type ShiftGroup = {
  shiftName: string;
  shiftStart?: string;
  positions: PositionGroup[];
  subtotal: number;
};

/**
 * Groups line items by shift > position, sorted by shift start time.
 * Items without shift/position data fall to an "Other" group at the bottom.
 */
export function groupLineItems(items: LineItem[]): ShiftGroup[] {
  // Group by shift_name
  const shiftMap = new Map<string, { shiftStart?: string; items: LineItem[] }>();

  for (const item of items) {
    const shiftKey = item.shift_name || "Other";
    if (!shiftMap.has(shiftKey)) {
      shiftMap.set(shiftKey, { shiftStart: item.shift_start, items: [] });
    }
    shiftMap.get(shiftKey)!.items.push(item);
    // Update shift_start if we find one
    if (item.shift_start && !shiftMap.get(shiftKey)!.shiftStart) {
      shiftMap.get(shiftKey)!.shiftStart = item.shift_start;
    }
  }

  // Build shift groups with position sub-groups
  const groups: ShiftGroup[] = [];

  for (const [shiftName, { shiftStart, items: shiftItems }] of shiftMap) {
    // Group by position_name within this shift
    const posMap = new Map<string, LineItem[]>();
    for (const item of shiftItems) {
      const posKey = item.position_name || "General";
      if (!posMap.has(posKey)) {
        posMap.set(posKey, []);
      }
      posMap.get(posKey)!.push(item);
    }

    const positions: PositionGroup[] = [];
    for (const [positionName, posItems] of posMap) {
      positions.push({
        positionName,
        items: posItems,
        subtotal: posItems.reduce((sum, i) => sum + (i.amount || 0), 0),
      });
    }

    groups.push({
      shiftName,
      shiftStart,
      positions,
      subtotal: positions.reduce((sum, p) => sum + p.subtotal, 0),
    });
  }

  // Sort: by shift_start (nulls/undefined last), "Other" always last
  groups.sort((a, b) => {
    if (a.shiftName === "Other") return 1;
    if (b.shiftName === "Other") return -1;
    if (a.shiftStart && b.shiftStart) return a.shiftStart.localeCompare(b.shiftStart);
    if (a.shiftStart) return -1;
    if (b.shiftStart) return 1;
    return a.shiftName.localeCompare(b.shiftName);
  });

  return groups;
}

/** Check if any line items have grouping data */
export function hasGroupingData(items: LineItem[]): boolean {
  return items.some((item) => item.shift_name || item.position_name);
}
